import React, { FC, useState } from 'react';
import NumberInput from '~ComponentsImproved/Numpad';
import { useI18n } from '~Hooks';
import { Box, Button, Card, Column, Columns, Divider } from '~Styles';
import Toggle from '~Components/Toggle';
import { DEFAULT_DATA, useStorage } from '~Components/Storage';
import styled from '@emotion/styled';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from '~Hooks';
import * as actions from '~actions';
import DevBranchModal from './DevBranchModal';
import { v4 as uuid } from 'uuid';

const Wrapper = styled(Box)`
  color: #fff;
`;

const STAGING_URL = 'http://om.baemingo.se/staging/';
const DEV_URL = 'http://development.baemingo.se';

const SettingsGroup = styled(Columns)`
  border-bottom: 1px solid #eee;
  margin-bottom: 16px;
  & > div > h3 {
    margin: 0;
  }
  @media (max-width: 420px) {
    flex-direction: column;
    font-size: 80%;
    & > div > h3 {
      margin-bottom: 16px;
    }
  }
`;

interface Props {
  goBack?: () => void;
}

const Developer: FC<Props> = ({ goBack }) => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const [storage, updateStorage] = useStorage();
  const [showFeatureBranchModal, setShowFeatureBranchModal] = useState(false);
  const history = useHistory();
  const venueId = useSelector((state) => state.venueId);

  const [showPasswordInput, setShowPasswordInput] = useState(true);

  if (showPasswordInput) {
    return (
      <div>
        <NumberInput
          onClose={() => history.push(`/venue/${venueId}/gateway`)}
          onSubmit={(value) => {
            if (value !== '8888') {
              dispatch(actions.addToast(i18n('Settings.NotAllowed'), true, 'notallowed', true));
              goBack && goBack();
            } else {
              setShowPasswordInput(false);
            }
          }}
        />
      </div>
    );
  }

  const handleStorageToggle = (key: string, value: boolean) => {
    updateStorage({
      [key]: value,
    });
  };

  const currentURL = window.location.toString();
  const onVercelBranch = currentURL.indexOf('vercel') > 0;
  const vercelBranch = currentURL
    .split('-')
    .slice(2, -1)
    .join('-');

  const goToHandy = () => {
    history.push(`/venue/${venueId}/handy`);
  };

  return (
    <Wrapper padding="large" fluid>
      <DevBranchModal
        open={showFeatureBranchModal}
        onClose={() => setShowFeatureBranchModal(false)}
      />
      <Card fluid backgroundColor="colorBlueGray1200">
        <Box as="h1" marginBottom="large">
          Developer page
        </Box>
        <Divider marginBottom="large" />
        <SettingsGroup>
          <Column>
            <h3>Tools</h3>
          </Column>
          <Column>
            <Columns alignItems="center" marginBottom="medium">
              <Column>Open feature branch</Column>
              <Column alignItems="flex-end">
                <Button
                  backgroundColor="colorBlack"
                  color="colorWhite"
                  onClick={() => setShowFeatureBranchModal(true)}>
                  Go
                </Button>
              </Column>
            </Columns>
            <Columns alignItems="center" marginBottom="medium">
              <Column>Go to staging</Column>
              <Column alignItems="flex-end">
                <Button
                  backgroundColor="colorBlack"
                  color="colorWhite"
                  onTouchStart={() => window.location.replace(STAGING_URL)}>
                  Go
                </Button>
              </Column>
            </Columns>
            <Columns alignItems="center" marginBottom="medium">
              <Column>Go to develop</Column>
              <Column alignItems="flex-end">
                <Button
                  backgroundColor="colorBlack"
                  color="colorWhite"
                  onTouchStart={() => window.location.replace(DEV_URL)}>
                  Go
                </Button>
              </Column>
            </Columns>
          </Column>
        </SettingsGroup>

        <SettingsGroup>
          <Column>
            <h3>Experimental features</h3>
          </Column>
          <Column>
            <Columns marginBottom="medium">
              <Column>LogRocket logging</Column>
              <Column alignItems="flex-end">
                <Toggle
                  label={storage.enableLogRocket ? 'Enabled' : 'Disabled'}
                  value={storage.enableLogRocket}
                  onClick={() => handleStorageToggle('enableLogRocket', !storage.enableLogRocket)}
                />
              </Column>
            </Columns>
          </Column>
        </SettingsGroup>
        <SettingsGroup>
          <Column>
            <h3>Info</h3>
          </Column>
          <Column>
            <Columns marginBottom="medium">
              <Column>Vercel branch</Column>
              <Column alignItems="flex-end" style={{ wordBreak: 'break-all' }}>
                {onVercelBranch ? vercelBranch : 'N/A'}
              </Column>
            </Columns>
            <Columns marginBottom="medium">
              <Column>Current URL</Column>
              <Column alignItems="flex-end" style={{ wordBreak: 'break-all' }}>
                {window.location.toString()}
              </Column>
            </Columns>
            <Columns marginBottom="medium">
              <Column>User-Agent</Column>
              <Column alignItems="flex-end" style={{ wordBreak: 'break-all' }}>
                {navigator.userAgent}
              </Column>
            </Columns>
            <Columns marginBottom="medium">
              <Column>Cookies enabled</Column>
              <Column alignItems="flex-end" style={{ wordBreak: 'break-all' }}>
                {navigator.cookieEnabled ? 'Enabled' : 'Disabled'}
              </Column>
            </Columns>
          </Column>
        </SettingsGroup>

        <SettingsGroup>
          <Column>
            <h3>Quick Navigation</h3>
          </Column>
          <Column>
            <Columns alignItems="center" marginBottom="medium">
              <Column width={3}>
                <Button backgroundColor="colorBlack" color="colorWhite" onTouchStart={goToHandy}>
                  Handy
                </Button>
              </Column>
            </Columns>
            <Columns alignItems="center" marginBottom="medium">
              <Column width={3}>
                <Button
                  backgroundColor="colorBlack"
                  color="colorWhite"
                  onTouchStart={() => history.push(`gateway`)}>
                  Gateway
                </Button>
              </Column>
            </Columns>
            <Columns alignItems="center" marginBottom="medium">
              <Column width={3}>
                <Button
                  backgroundColor="colorBlack"
                  color="colorWhite"
                  onTouchStart={() => history.push(`settings`)}>
                  Settings
                </Button>
              </Column>
            </Columns>
          </Column>
        </SettingsGroup>

        <SettingsGroup>
          <Column>
            <h3>Debug</h3>
          </Column>
          <Column>
            <Columns alignItems="center" marginBottom="medium">
              <Column>
                Dev mode: Shows more information in pages and can be used for extra logging
                purposes
              </Column>
              <Column alignItems="flex-end">
                <Toggle
                  label={storage.devMode ? 'Enabled' : 'Disabled'}
                  value={storage.devMode}
                  onClick={() => handleStorageToggle('devMode', !storage.devMode)}
                />
              </Column>
            </Columns>
          </Column>
        </SettingsGroup>

        <SettingsGroup>
          <Column>
            <h3>DEBUG: Generate new clientId (use with caution)</h3>
          </Column>
          <Column>
            <Columns alignItems="center" marginBottom="medium">
              <Column>
                Generates a new unique identifier for this device. This will remove the device in
                use if the company is on new licenses
              </Column>
              <Column alignItems="flex-end">
                <Button
                  backgroundColor="colorBlack"
                  color="colorWhite"
                  onTouchStart={() => {
                    updateStorage({ clientId: uuid() });
                    dispatch(actions.addToast('Client ID reset', true));
                  }}>
                  Generate new clientId
                </Button>
              </Column>
            </Columns>
          </Column>
        </SettingsGroup>

        <SettingsGroup>
          <Column>
            <h3>Clear localStorage (be careful)</h3>
          </Column>
          <Column>
            <Columns alignItems="center" marginBottom="medium">
              <Column>
                Clears all local storage for this device. This will reset all settings and needs to
                be used with caution.
              </Column>
              <Column alignItems="flex-end">
                <Button
                  backgroundColor="colorBlack"
                  color="colorWhite"
                  onTouchStart={() => {
                    updateStorage(DEFAULT_DATA);
                    dispatch(actions.addToast('Local storage cleared', true));
                  }}>
                  Reset
                </Button>
              </Column>
            </Columns>
          </Column>
        </SettingsGroup>
      </Card>
    </Wrapper>
  );
};

export default Developer;
